import React, { useState, useEffect } from "react";
import api from "../../services/api";
import { toast } from "react-toastify";
import { LuPlus, LuPencil, LuTrash2, LuSearch, LuChevronLeft, LuChevronRight, LuKey } from "react-icons/lu";

const UserManagement = ({ isSidebarOpen = true }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState("add"); // "add" or "edit"

    // Filters & Pagination State
    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("All");
    const [statusFilter, setStatusFilter] = useState("All");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    // Form State
    const [formData, setFormData] = useState({
        id: null,
        userName: "",
        email: "",
        password: "", // Only for add
        mobileNo: "",
        role: "USER",
        permissions: [],
        status: "active"
    });

    const availablePermissions = ["Trade Book", "Algo", "Logs"];

    useEffect(() => {
        setPage(1); // Reset to page 1 on filter/limit change
    }, [search, roleFilter, statusFilter, limit]);

    useEffect(() => {
        fetchUsers();
    }, [page, limit]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const params = { page, limit };
            if (search) params.search = search;
            if (roleFilter !== 'All') params.role = roleFilter;
            if (statusFilter !== 'All') params.status = statusFilter;

            const response = await api.get("/admin/users", { params });
            if (response.data.status === "success") {
                setUsers(response.data.data);
                if (response.data.pagination) {
                    setTotalPages(response.data.pagination.pages);
                    setTotalCount(response.data.pagination.total || 0);
                }
            } else {
                toast.error(response.data.message);
                setUsers([]);
            }
        } catch (error) {
            console.error("Fetch Error:", error);
            toast.error("Failed to fetch users");
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handlePermissionChange = (perm) => {
        const currentPerms = formData.permissions;
        if (currentPerms.includes(perm)) {
            setFormData({ ...formData, permissions: currentPerms.filter(p => p !== perm) });
        } else {
            setFormData({ ...formData, permissions: [...currentPerms, perm] });
        }
    };

    const applyFilters = () => {
        setPage(1);
        fetchUsers();
    };

    const resetFilters = () => {
        setSearch("");
        setRoleFilter("All");
        setStatusFilter("All");
        setPage(1);
        // We can optionally fetch immediately after reset or let user click apply.
        // OrderList resets and then auto fetches via timeout.
        // Let's mimic auto-fetch on reset for convenience.
        setTimeout(() => fetchUsers(), 0);
    };

    const openAddModal = () => {
        setModalMode("add");
        setFormData({
            id: null,
            userName: "",
            email: "",
            password: "",
            mobileNo: "",
            role: "USER",
            permissions: [],
            status: "active"
        });
        setShowModal(true);
    };

    const openEditModal = (user) => {
        setModalMode("edit");
        let initialPermissions = [];
        try {
            if (user.user_permissions) initialPermissions = JSON.parse(user.user_permissions);
        } catch (e) { }

        setFormData({
            id: user.user_id,
            userName: user.user_name,
            email: user.user_email,
            password: "", // User can't edit password here easily
            mobileNo: user.user_mobile_no,
            role: user.user_role,
            permissions: initialPermissions,
            status: user.user_status
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            let response;
            if (modalMode === "add") {
                response = await api.post("/admin/add/user", formData);
            } else {
                response = await api.put(`/admin/user/${formData.id}`, formData);
            }

            if (response.data.status === "success") {
                toast.success(response.data.message);
                setShowModal(false);
                fetchUsers();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error("Operation failed");
        }
    };

    const handleDelete = async (userId) => {
        if (window.confirm("Are you sure you want to delete this user? This cannot be undone.")) {
            try {
                const response = await api.delete(`/admin/user/${userId}`);
                if (response.data.status === "success") {
                    toast.success(response.data.message);
                    fetchUsers();
                } else {
                    toast.error(response.data.message);
                }
            } catch (error) {
                toast.error("Delete failed");
            }
        }
    };

    // Reset Password State
    const [showResetModal, setShowResetModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [resetPassword, setResetPassword] = useState("");
    const [confirmResetPassword, setConfirmResetPassword] = useState("");

    const openResetModal = (user) => {
        setSelectedUser(user);
        setResetPassword("");
        setConfirmResetPassword("");
        setShowResetModal(true);
    };

    const handleResetPasswordSubmit = async (e) => {
        e.preventDefault();
        if (resetPassword !== confirmResetPassword) {
            toast.error("Passwords do not match");
            return;
        }

        try {
            const response = await api.put(`/admin/user/${selectedUser.user_id}/password`, {
                newPassword: resetPassword
            });

            if (response.data.status === "success") {
                toast.success(response.data.message);
                setShowResetModal(false);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error("Reset Password Error:", error);
            toast.error("Failed to reset password");
        }
    };

    // Download Database
    const handleDownloadDb = async () => {
        try {
            const response = await api.get(
                "/admin/download-db",
                {
                    responseType: 'blob', // Important for file download
                }
            );

            // Create a URL for the blob
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'database.zip'); // File name
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("Database downloaded successfully");
        } catch (error) {
            console.error("Download failed:", error);
            toast.error("Failed to download database");
        }
    };

    // Helper to generate page numbers
    const paginationRange = () => {
        const totalPageCount = totalPages;
        const siblingCount = 1;
        const boundaryCount = 3;

        if (totalPageCount <= 10) {
            const range = [];
            for (let i = 1; i <= totalPageCount; i++) range.push(i);
            return range;
        }

        const leftSiblingIndex = Math.max(page - siblingCount, 1);
        const rightSiblingIndex = Math.min(page + siblingCount, totalPageCount);

        const shouldShowLeftDots = leftSiblingIndex > boundaryCount + 1;
        const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

        if (!shouldShowLeftDots && shouldShowRightDots) {
            const leftItemCount = 3 + 2 * siblingCount;
            let leftRange = [];
            for (let i = 1; i <= Math.max(leftItemCount, rightSiblingIndex); i++) leftRange.push(i);
            return [...leftRange, "...", totalPageCount];
        }

        if (shouldShowLeftDots && !shouldShowRightDots) {
            const rightItemCount = 3 + 2 * siblingCount;
            let rightRange = [];
            for (let i = totalPageCount - rightItemCount + 1; i <= totalPageCount; i++) rightRange.push(i);
            return [1, 2, 3, "...", ...rightRange];
        }

        if (shouldShowLeftDots && shouldShowRightDots) {
            let middleRange = [];
            for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) middleRange.push(i);
            return [1, 2, 3, "...", ...middleRange, "...", totalPageCount];
        }
    };

    return (
        <div className={`${isSidebarOpen ? "col-lg-9" : "col-lg-12"} pt-4 pb-2 pb-sm-4`}>
            <div className="container">
                <div className="row">
                    <div className="col-12 mb-4">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                            <h1 className="h3 mb-0 text-gray-800">User Management</h1>
                            <button className="btn btn-sm btn-primary d-flex align-items-center gap-2" onClick={openAddModal}>
                                <LuPlus size={16} /> Add User
                            </button>
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="card border-0 py-1 p-md-2 p-xl-3 p-xxl-4 mb-4">
                            <div className="card-body">
                                <h6 className="fw-bold mb-3">Filters</h6>
                                <div className="row g-3">
                                    <div className="col-12 col-sm-6 col-lg-3">
                                        <label className="form-label small text-muted mb-1">Search</label>
                                        <div className="input-group input-group-sm p-1">
                                            <span className="input-group-text"><LuSearch size={14} /></span>
                                            <input
                                                type="text"
                                                className="form-control p-0"
                                                placeholder="Name, Email..."
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 col-sm-6 col-lg-3">
                                        <label className="form-label small text-muted mb-1">Role</label>
                                        <select
                                            className="form-select"
                                            value={roleFilter}
                                            onChange={(e) => setRoleFilter(e.target.value)}
                                        >
                                            <option value="All">All Roles</option>
                                            <option value="ADMIN">Admin</option>
                                            <option value="USER">User</option>
                                        </select>
                                    </div>
                                    <div className="col-12 col-sm-6 col-lg-3">
                                        <label className="form-label small text-muted mb-1">Status</label>
                                        <select
                                            className="form-select"
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                        >
                                            <option value="All">All Status</option>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </div>
                                    <div className="col-12 col-sm-6 col-lg-3 d-flex align-items-end">
                                        <div className="d-flex gap-2 w-100">
                                            <button className="btn btn-sm btn-primary w-50" onClick={applyFilters} disabled={loading}>
                                                {loading ? "..." : "Apply"}
                                            </button>
                                            <button className="btn btn-sm btn-outline-primary w-50" onClick={resetFilters}>
                                                Reset
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="text-muted small">
                                <strong>Total Users: {totalCount}</strong>
                                <span className="mx-2">|</span>
                                Showing {(page - 1) * limit + 1} - {Math.min(page * limit, totalCount)}
                            </div>
                            <div className="text-muted small">
                                Page {page} of {totalPages}
                            </div>
                        </div>
                    </div>

                    <div className="col-12">
                        <section className="card border-0 py-1 p-md-2 p-xl-3 p-xxl-4 mb-4">
                            <div className="card-body">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="text-muted mt-2">Loading users...</p>
                                    </div>
                                ) : users.length === 0 ? (
                                    <div className="text-center py-5">
                                        <h5 className="text-muted">No users found.</h5>
                                    </div>
                                ) : (
                                    <>
                                        <div className="overflow-auto">
                                            <table className="table table-hover align-middle mb-0">
                                                <thead>
                                                    <tr>
                                                        <th className="ps-4 rounded-start">Name</th>
                                                        <th>Email</th>
                                                        <th>Role</th>

                                                        <th>Status</th>
                                                        <th className="text-end rounded-end pe-4">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {users.map((user) => (
                                                        <tr key={user.user_id}>
                                                            <td className="ps-4 fw-medium">{user.user_name}</td>
                                                            <td>{user.user_email}</td>
                                                            <td>
                                                                <span className={`badge ${user.user_role === 'ADMIN' ? 'bg-primary' : 'bg-info'}`}>
                                                                    {user.user_role}
                                                                </span>
                                                            </td>

                                                            <td>
                                                                <span className={`badge ${user.user_status === 'Y' ? 'bg-success' : 'bg-secondary'}`}>
                                                                    {user.user_status}
                                                                </span>
                                                            </td>
                                                            <td className="text-end pe-4">
                                                                <button className="btn btn-sm btn-outline-primary me-2" onClick={() => openEditModal(user)}>
                                                                    <LuPencil size={14} />
                                                                </button>
                                                                <button className="btn btn-sm btn-outline-warning me-2" onClick={() => openResetModal(user)} title="Reset Password">
                                                                    <LuKey size={14} />
                                                                </button>
                                                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(user.user_id)}>
                                                                    <LuTrash2 size={14} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Pagination */}
                                        <div className="d-flex align-items-center justify-content-between pt-3 pb-2 px-3 border-top">
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="text-body-secondary fs-sm">Show</span>
                                                <select
                                                    className="form-select form-select-sm form-select-flush"
                                                    style={{ width: 'auto' }}
                                                    value={limit}
                                                    onChange={(e) => setLimit(parseInt(e.target.value))}
                                                >
                                                    <option value="10">10</option>
                                                    <option value="25">25</option>
                                                    <option value="50">50</option>
                                                    <option value="100">100</option>
                                                </select>
                                            </div>

                                            <nav aria-label="Page navigation">
                                                <ul className="pagination pagination-sm mb-0">
                                                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => setPage(page - 1)}
                                                            aria-label="Prev"
                                                        >
                                                            <LuChevronLeft className="fs-xl me-1" /> Prev
                                                        </button>
                                                    </li>

                                                    {paginationRange().map((p, idx) => (
                                                        <li key={idx} className={`page-item ${p === page ? 'active' : ''} ${p === '...' ? 'disabled' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => p !== '...' && setPage(p)}
                                                                style={{ cursor: p === '...' ? 'default' : 'pointer' }}
                                                            >
                                                                {p}
                                                            </button>
                                                        </li>
                                                    ))}

                                                    <li className={`page-item ${page === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => setPage(page + 1)}
                                                            aria-label="Next"
                                                        >
                                                            Next <LuChevronRight className="fs-xl ms-1" />
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        </div>
                                    </>
                                )}
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{modalMode === "add" ? "Add User" : "Edit User"}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Full Name</label>
                                        <input type="text" className="form-control" name="userName" value={formData.userName} onChange={handleInputChange} required />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Email</label>
                                        <input type="email" className="form-control" name="email" value={formData.email} onChange={handleInputChange} required />
                                    </div>
                                    {modalMode === "add" && (
                                        <div className="mb-3">
                                            <label className="form-label">Password</label>
                                            <input type="password" className="form-control" name="password" value={formData.password} onChange={handleInputChange} required />
                                        </div>
                                    )}
                                    <div className="mb-3">
                                        <label className="form-label">Mobile No</label>
                                        <input type="text" className="form-control" name="mobileNo" value={formData.mobileNo} onChange={handleInputChange} required />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Role</label>
                                        <select className="form-select" name="role" value={formData.role} onChange={handleInputChange}>
                                            <option value="USER">USER</option>
                                            <option value="ADMIN">ADMIN</option>
                                        </select>
                                    </div>

                                    {formData.role === 'USER' && (
                                        <div className="mb-3">
                                            <label className="form-label d-block">Page Permissions</label>
                                            <div className="d-flex gap-3">
                                                {availablePermissions.map(perm => (
                                                    <div className="form-check" key={perm}>
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id={`perm-${perm}`}
                                                            checked={formData.permissions.includes(perm)}
                                                            onChange={() => handlePermissionChange(perm)}
                                                        />
                                                        <label className="form-check-label" htmlFor={`perm-${perm}`}>
                                                            {perm}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="mb-3">
                                        <label className="form-label">Status</label>
                                        <select className="form-select" name="status" value={formData.status} onChange={handleInputChange}>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </div>

                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary">{modalMode === "add" ? "Create User" : "Save Changes"}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
            {/* Reset Password Modal */}
            {showResetModal && (
                <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Reset Password for {selectedUser?.user_name}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowResetModal(false)}></button>
                            </div>
                            <form onSubmit={handleResetPasswordSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">New Password</label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            value={resetPassword}
                                            onChange={(e) => setResetPassword(e.target.value)}
                                            required
                                            minLength={6}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Confirm Password</label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            value={confirmResetPassword}
                                            onChange={(e) => setConfirmResetPassword(e.target.value)}
                                            required
                                            minLength={6}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowResetModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-warning ms-2">Reset Password</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

};

export default UserManagement;
